/**
 * Security Tree — Pure Logic (framework-agnostic)
 *
 * All state machine logic for the 5-state security tree.
 * This module has NO React dependencies — it's pure functions for testability.
 */

import type {
  AccessState,
  ExplicitAccessState,
  PlantAccessEntry,
  SecurityTreeNodeDto,
  TreeFilter,
  TreeNode,
} from './types';

// ─── Tree Construction ─────────────────────────────────────────────────────

/** Build the internal tree structure from flat API response. */
export function buildTree(nodes: SecurityTreeNodeDto[]): Map<string, TreeNode> {
  const tree = new Map<string, TreeNode>();

  // First pass: create all nodes
  for (const dto of nodes) {
    tree.set(dto.id, {
      id: dto.id,
      originId: dto.originId,
      nodeType: dto.nodeType.toLowerCase() as TreeNode['nodeType'],
      label: dto.label,
      parentId: dto.parentId,
      childIds: [],
      accessState: dto.accessState.toLowerCase() as AccessState,
      siblingIds: [],
    });
  }

  // Second pass: wire parent→child relationships
  for (const node of tree.values()) {
    if (node.parentId && tree.has(node.parentId)) {
      tree.get(node.parentId)!.childIds.push(node.id);
    }
  }

  // Third pass: wire plant sibling IDs (same originId, different node ID)
  const plantsByOrigin = new Map<number, string[]>();
  for (const node of tree.values()) {
    if (node.nodeType === 'plant') {
      const existing = plantsByOrigin.get(node.originId) ?? [];
      existing.push(node.id);
      plantsByOrigin.set(node.originId, existing);
    }
  }
  for (const siblingIds of plantsByOrigin.values()) {
    if (siblingIds.length > 1) {
      for (const id of siblingIds) {
        tree.get(id)!.siblingIds = siblingIds.filter(s => s !== id);
      }
    }
  }

  // Fourth pass: compute parent states from loaded leaf access
  computeAllParentStates(tree);

  return tree;
}

// ─── State Transitions ─────────────────────────────────────────────────────

/** The click cycle for explicit states. */
const STATE_CYCLE: ExplicitAccessState[] = ['none', 'read', 'write'];

/**
 * Get the next explicit state in the click cycle.
 * Partial states ARE clickable — they represent the "level" of their content:
 *   partial-read ≈ read-level → next is write
 *   partial-write ≈ write-level → next is none
 */
export function nextState(current: AccessState): ExplicitAccessState {
  if (current === 'partial-read') return 'write';
  if (current === 'partial-write') return 'none';
  const idx = STATE_CYCLE.indexOf(current as ExplicitAccessState);
  return STATE_CYCLE[(idx + 1) % STATE_CYCLE.length];
}

/**
 * Toggle a node's access state (click handler logic).
 * Returns a new Map with the updated tree.
 */
export function toggleNode(tree: Map<string, TreeNode>, nodeId: string): Map<string, TreeNode> {
  const node = tree.get(nodeId);
  if (!node) return tree;

  // Clone the map for immutability
  const newTree = cloneTree(tree);
  const targetNode = newTree.get(nodeId)!;
  const newState = nextState(targetNode.accessState);

  // Apply state to this node
  targetNode.accessState = newState;

  // Propagate DOWN: set all descendants to the same state
  propagateDown(newTree, nodeId, newState);

  // Sync siblings (multi-path plants)
  if (targetNode.nodeType === 'plant') {
    for (const sibId of targetNode.siblingIds) {
      const sib = newTree.get(sibId)!;
      sib.accessState = newState;
      // Propagate UP from each sibling's parent
      propagateUp(newTree, sib.parentId);
    }
  }

  // Propagate UP: recalculate parent states
  propagateUp(newTree, targetNode.parentId);

  return newTree;
}

// ─── Propagation ────────────────────────────────────────────────────────────

/** Set all descendants of a node to the given state. */
function propagateDown(tree: Map<string, TreeNode>, nodeId: string, state: ExplicitAccessState): void {
  const node = tree.get(nodeId);
  if (!node) return;

  for (const childId of node.childIds) {
    const child = tree.get(childId)!;
    child.accessState = state;

    // Sync plant siblings
    if (child.nodeType === 'plant') {
      for (const sibId of child.siblingIds) {
        tree.get(sibId)!.accessState = state;
      }
    }

    propagateDown(tree, childId, state);
  }
}

/** Recalculate a parent's state from its children, then recurse up. */
function propagateUp(tree: Map<string, TreeNode>, parentId: string | null): void {
  if (!parentId) return;

  const parent = tree.get(parentId);
  if (!parent) return;

  parent.accessState = computeParentState(tree, parent);
  propagateUp(tree, parent.parentId);
}

/** Compute a parent node's state from its children. */
function computeParentState(tree: Map<string, TreeNode>, parent: TreeNode): AccessState {
  if (parent.childIds.length === 0) return 'none';

  const childStates = parent.childIds.map(id => tree.get(id)!.accessState);

  // Check if all children have the same explicit state
  const first = childStates[0];
  if (childStates.every(s => s === first)) {
    // If all same and it's an explicit state, parent gets that state
    if (first === 'none' || first === 'read' || first === 'write') {
      return first;
    }
  }

  // Mixed states — determine partial variant
  const hasWrite = childStates.some(s => s === 'write' || s === 'partial-write');
  if (hasWrite) return 'partial-write';

  const hasRead = childStates.some(s => s === 'read' || s === 'partial-read');
  if (hasRead) return 'partial-read';

  return 'none';
}

/** Recompute all parent states bottom-up. */
function computeAllParentStates(tree: Map<string, TreeNode>): void {
  // Find root nodes (no parent) and recurse
  for (const node of tree.values()) {
    if (!node.parentId) {
      computeSubtreeStates(tree, node);
    }
  }
}

/** Recursively compute state for a subtree (post-order). */
function computeSubtreeStates(tree: Map<string, TreeNode>, node: TreeNode): void {
  if (node.childIds.length === 0) return; // leaves keep their loaded state

  for (const childId of node.childIds) {
    computeSubtreeStates(tree, tree.get(childId)!);
  }

  node.accessState = computeParentState(tree, node);
}

// ─── Filtering ──────────────────────────────────────────────────────────────

/**
 * Get the set of visible node IDs based on filter criteria.
 * Also recomputes parent states for visible parents based on only their visible children.
 * This fixes the Magma bug where filtered parent nodes showed stale states.
 */
export function getVisibleNodeIds(tree: Map<string, TreeNode>, filter: TreeFilter): Set<string> {
  if (!filter.areaId && !filter.countryId && !filter.regionId) {
    // No filter — all visible
    return new Set(tree.keys());
  }

  const visible = new Set<string>();

  for (const node of tree.values()) {
    let matches = true;

    if (filter.regionId) {
      if (node.nodeType === 'plant') {
        // Plant must be under the specified region
        matches = node.parentId === `region-${filter.regionId}`;
      } else if (node.nodeType === 'region') {
        matches = node.originId === filter.regionId;
      } else if (node.nodeType === 'country') {
        // Show country if it contains the filtered region
        const region = tree.get(`region-${filter.regionId}`);
        matches = region?.parentId === node.id;
      } else if (node.nodeType === 'area') {
        // Show area if it's ancestor of the filtered region
        const region = tree.get(`region-${filter.regionId}`);
        const country = region?.parentId ? tree.get(region.parentId) : undefined;
        matches = country?.parentId === node.id;
      }
    } else if (filter.countryId) {
      if (node.nodeType === 'plant') {
        const region = tree.get(node.parentId!);
        matches = region?.parentId === `country-${filter.countryId}`;
      } else if (node.nodeType === 'region') {
        matches = node.parentId === `country-${filter.countryId}`;
      } else if (node.nodeType === 'country') {
        matches = node.originId === filter.countryId;
      } else if (node.nodeType === 'area') {
        // Show area if it contains the filtered country
        const country = [...tree.values()].find(
          n => n.nodeType === 'country' && n.originId === filter.countryId
        );
        matches = country?.parentId === node.id;
      }
    } else if (filter.areaId) {
      if (node.nodeType === 'area') {
        matches = node.originId === filter.areaId;
      } else {
        // Walk up to area
        matches = isDescendantOfArea(tree, node, filter.areaId);
      }
    }

    if (matches) {
      visible.add(node.id);
      // Add all ancestors to ensure tree structure is visible
      addAncestors(tree, node, visible);
    }
  }

  return visible;
}

function isDescendantOfArea(tree: Map<string, TreeNode>, node: TreeNode, areaId: number): boolean {
  let current: TreeNode | undefined = node;
  while (current?.parentId) {
    current = tree.get(current.parentId);
    if (current?.nodeType === 'area' && current.originId === areaId) return true;
  }
  return false;
}

function addAncestors(tree: Map<string, TreeNode>, node: TreeNode, visible: Set<string>): void {
  let parentId = node.parentId;
  while (parentId) {
    visible.add(parentId);
    parentId = tree.get(parentId)?.parentId ?? null;
  }
}

/**
 * Recompute parent states based on only their visible children.
 * This ensures that when a filter hides some children, the parent state
 * reflects the states of only the visible subset — fixing the Magma bug.
 */
export function computeFilteredParentStates(
  tree: Map<string, TreeNode>,
  visible: Set<string>,
): Map<string, TreeNode> {
  // Clone tree to avoid mutating the source
  const filtered = cloneTree(tree);

  // Process bottom-up: find visible parents whose state should be recomputed
  // Only recompute nodes that are NOT leaves and ARE visible
  const parents = [...filtered.values()]
    .filter(n => n.childIds.length > 0 && visible.has(n.id))
    .sort((a, b) => depth(filtered, b) - depth(filtered, a)); // deepest first

  for (const parent of parents) {
    const visibleChildStates = parent.childIds
      .filter(id => visible.has(id))
      .map(id => filtered.get(id)!.accessState);

    if (visibleChildStates.length === 0) continue;

    const first = visibleChildStates[0];
    if (visibleChildStates.every(s => s === first)) {
      if (first === 'none' || first === 'read' || first === 'write') {
        parent.accessState = first;
        continue;
      }
    }

    const hasWrite = visibleChildStates.some(s => s === 'write' || s === 'partial-write');
    if (hasWrite) {
      parent.accessState = 'partial-write';
      continue;
    }

    const hasRead = visibleChildStates.some(s => s === 'read' || s === 'partial-read');
    parent.accessState = hasRead ? 'partial-read' : 'none';
  }

  return filtered;
}

function depth(tree: Map<string, TreeNode>, node: TreeNode): number {
  let d = 0;
  let current: TreeNode | undefined = node;
  while (current?.parentId) {
    d++;
    current = tree.get(current.parentId);
  }
  return d;
}

// ─── Change Extraction ──────────────────────────────────────────────────────

/**
 * Extract the save payload — only plants with Read or Write access.
 * Applies most-permissive rule for multi-path plants.
 */
export function getChangedAccess(tree: Map<string, TreeNode>): PlantAccessEntry[] {
  const plantAccess = new Map<number, 'READ' | 'WRITE'>();

  for (const node of tree.values()) {
    if (node.nodeType !== 'plant') continue;
    if (node.accessState === 'none') continue;

    const level: 'READ' | 'WRITE' = node.accessState === 'write' ? 'WRITE' : 'READ';

    // Most-permissive: Write wins over Read
    const existing = plantAccess.get(node.originId);
    if (!existing || (existing === 'READ' && level === 'WRITE')) {
      plantAccess.set(node.originId, level);
    }
  }

  return [...plantAccess.entries()].map(([plantId, accessLevel]) => ({
    plantId,
    accessLevel,
  }));
}

/**
 * Compare current tree state to initial state to determine if anything changed.
 */
export function hasChanges(
  currentTree: Map<string, TreeNode>,
  initialTree: Map<string, TreeNode>
): boolean {
  const current = getChangedAccess(currentTree);
  const initial = getChangedAccess(initialTree);

  if (current.length !== initial.length) return true;

  const initialMap = new Map(initial.map(e => [e.plantId, e.accessLevel]));
  return current.some(
    e => initialMap.get(e.plantId) !== e.accessLevel
  );
}

// ─── Utilities ──────────────────────────────────────────────────────────────

/** Deep clone the tree map (shallow clone of each node). */
function cloneTree(tree: Map<string, TreeNode>): Map<string, TreeNode> {
  const newTree = new Map<string, TreeNode>();
  for (const [id, node] of tree) {
    newTree.set(id, { ...node, childIds: [...node.childIds], siblingIds: [...node.siblingIds] });
  }
  return newTree;
}

/** Get root nodes (nodes with no parent). */
export function getRootNodes(tree: Map<string, TreeNode>): TreeNode[] {
  return [...tree.values()].filter(n => !n.parentId);
}

/** Get children of a node. */
export function getChildren(tree: Map<string, TreeNode>, nodeId: string): TreeNode[] {
  const node = tree.get(nodeId);
  if (!node) return [];
  return node.childIds.map(id => tree.get(id)!);
}
